import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type { BaseAction } from '../@types/actions';
import type { FinancialConfiguration, FinancialInformation } from '../@types/finance';
import * as http from '../Http';
import { buildPostRequest } from '../Http/requestTemplates';
import type { RootState } from '../reducers';
import requestCache from '../utils/request-cache';

export const REQ_GET_CURRENT_FINANCE_INFORMATION = 'REQ/GET_CURRENT_FINANCE_INFORMATION';
export const RCV_GET_CURRENT_FINANCE_INFORMATION = 'RCV/GET_CURRENT_FINANCE_INFORMATION';
type REQ_GET_CURRENT_FINANCE_INFORMATION_TYPE = BaseAction<
  typeof REQ_GET_CURRENT_FINANCE_INFORMATION
>;
type RCV_GET_CURRENT_FINANCE_INFORMATION_TYPE = BaseAction<
  typeof RCV_GET_CURRENT_FINANCE_INFORMATION
> & {
  response: FinancialInformation;
};

export const RESET_CURRENT_FINANCE_INFORMATION =
  'CURRENT_FINANCE_INFORMATION/RESET_CURRENT_FINANCE_INFORMATION';
type RESET_CURRENT_FINANCE_INFORMATION_TYPE = BaseAction<typeof RESET_CURRENT_FINANCE_INFORMATION>;

export const REQUEST_FAILED = 'CURRENT_FINANCE_INFORMATION/REQUEST_FAILED';

type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action: typeof REQ_GET_CURRENT_FINANCE_INFORMATION;
};

export type CURRENT_FINANCIAL_INFORMATION_ACTION =
  | REQ_GET_CURRENT_FINANCE_INFORMATION_TYPE
  | RCV_GET_CURRENT_FINANCE_INFORMATION_TYPE
  | RESET_CURRENT_FINANCE_INFORMATION_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<CURRENT_FINANCIAL_INFORMATION_ACTION>;

export const getCurrentFinancialInformation =
  (vehicleId: string, _configuration: FinancialConfiguration, branchId?: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { currentFinancialInformation, defaultFinancialInformation } = state;

    const configuration: FinancialConfiguration = {
      downPayment: Math.round(_configuration.downPayment),
      duration: Math.round(_configuration.duration),
      residual: _configuration.residual,
    };

    const isValidPayload = isValid(
      configuration,
      currentFinancialInformation,
      defaultFinancialInformation,
    );
    if (!isValidPayload) {
      return;
    }

    if (requestCache.isPending(REQ_GET_CURRENT_FINANCE_INFORMATION)) {
      return;
    }
    requestCache.isPending(REQ_GET_CURRENT_FINANCE_INFORMATION, true);

    try {
      dispatch({ type: REQ_GET_CURRENT_FINANCE_INFORMATION });

      const response = await http.json<FinancialInformation>(
        `/api/financial-information/${vehicleId}${branchId ? `?branchId=${branchId}` : ''}`,
        buildPostRequest(configuration),
      );
      http.validateResponse(response);
      dispatch({
        type: RCV_GET_CURRENT_FINANCE_INFORMATION,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_GET_CURRENT_FINANCE_INFORMATION });
    }
    requestCache.isPending(REQ_GET_CURRENT_FINANCE_INFORMATION, false);
  };

const isValid = (
  configuration: FinancialConfiguration,
  currentFinancialInformation: any,
  defaultFinancialInformation: any,
): boolean => {
  const financialInformation: FinancialInformation | null =
    currentFinancialInformation.current || defaultFinancialInformation.default;

  if (!configuration || !financialInformation) {
    return false;
  }

  const { duration, residual, downPayment } = financialInformation;

  let isValidDuration = true;
  let isValidResidual = true;
  let isValidDownPayment = true;

  if (duration) {
    isValidDuration =
      configuration.duration >= duration.min && configuration.duration <= duration.max;
  }

  if (residual) {
    isValidResidual =
      configuration.residual >= residual.min && configuration.residual <= residual.max;
  }

  if (downPayment) {
    isValidDownPayment =
      configuration.downPayment >= downPayment.min && configuration.downPayment <= downPayment.max;
  }

  return isValidDuration && isValidResidual && isValidDownPayment;
};

export const resetCurrentFinancialInformation = () => (dispatch: Dispatch) => {
  dispatch({ type: RESET_CURRENT_FINANCE_INFORMATION });
};
